import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui';
import bugsnagClient from '../config/bugsnag';
import type { Event as BugsnagEvent } from '@bugsnag/js';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Only unambiguous authentication failures should hard-redirect to /login.
 *
 * This used to match any message containing the substrings "auth" or "session", which caught
 * plenty of unrelated errors — "failed to save session notes", "authorization matrix", and so
 * on — and silently threw the user out of the app instead of showing the error. With 401s now
 * flowing through the sessionExpiry signal, this boundary only needs to catch the narrow case
 * of an auth error surfacing during render.
 *
 * Note "401" is matched as a standalone token so that ids, totals and timestamps that merely
 * contain the digits (task 40199, $401.50) do not trigger a redirect.
 */
function isAuthRelatedError(error: Error): boolean {
  const msg = error?.message || '';
  const name = error?.name || '';
  return (
    /\b401\b/.test(msg) ||
    /unauthorized/i.test(msg) ||
    /^authenticationerror$/i.test(name)
  );
}

class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error boundary caught an error:', error, errorInfo);
    bugsnagClient.notify(error, (event: BugsnagEvent) => {
      event.addMetadata('react', {
        componentStack: errorInfo.componentStack,
      });
      return true;
    });
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (isAuthRelatedError(this.state.error)) {
        return <Navigate to="/login" />;
      }
      return (
        <div className="tw-p-4 tw-text-center">
          <h5>Something went wrong</h5>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            {this.state.error.message || 'An unexpected error occurred.'}
          </p>
          <Button as={Link} to="/home" variant="primary">
            Go to Home
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary; 